import React, {useCallback, useState} from "react";
import {Button} from "@aws-amplify/ui-react";
import {BookmarkAdd, BookmarkRemove} from "@mui/icons-material";
import {CircularProgress} from "@mui/material";
import {toast} from "react-toastify";
import {useTrackingProduct} from "../application/useTrackingProduct";

/**
 * @typedef {Object} TrackProductButtonProps
 * @property {boolean} isTracked - Whether the product is already tracked.
 * @property {() => Promise<void>} onTrack - Function to track the product.
 * @property {() => Promise<void>} onUntrack - Function to untrack the product.
 */

/**
 * TrackProductButton Component: Button to track/untrack a product, with loading state.
 * @param {{
 *     productCode: string,
 *     defaultValue: boolean,
 * }} props
 */
export const TrackProductButton = ({productCode, defaultValue, showText = false, quantity = 1}) => {
    const {isTracked, handleTrack, handleUntrack} = useTrackingProduct(productCode, defaultValue);
    const [loading, setLoading] = useState(false);

    const handleClick = useCallback(async () => {
        setLoading(true);
        try {
            if (isTracked) {
                await handleUntrack(
                    () => toast.success("Producto quitado de tus productos pendientes!"),
                    () => toast.error("Error al quitar producto de tus productos pendientes")
                )
            } else {
                await handleTrack(
                    () => toast.success("Producto agregado a tus productos pendientes!"),
                    () => toast.error("Error al agregar producto a tus productos pendientes"),
                    quantity
                )
            }
        } catch (error) {
            console.error('Failed to track/untrack product:', error);
        } finally {
            setLoading(false);
        }
    }, [isTracked, handleTrack, handleUntrack, quantity]);

    const getButtonIcon = () => {
        if (loading) {
            return <CircularProgress size={20}/>;
        }
        return isTracked ? <BookmarkRemove/> : <BookmarkAdd/>;
    };

    return (
        <div className="d-flex gap-1 align-items-center">
            <Button
                variant="contained"
                className="btn btn-warning"
                onClick={handleClick}
                disabled={loading}
            >
                {getButtonIcon()}
            </Button>
            {showText && <span className="text-warning">{isTracked ? 'Quitar de' : 'Añadir a'} pendientes</span>}
        </div>

    );
};
